import React, { useCallback, useEffect, useState } from 'react';
import {
  MembershipPlan,
  fetchMembershipGstSettings,
  fetchMembershipPlans,
  manualActivateMembership,
  runMembershipExpiryJob,
  seedMembershipPlans,
  updateMembershipGstSettings,
} from '../../../services/MasterAdmin/membershipService';
import MembershipPanelShell from './MembershipPanelShell';
import { MembershipError, MembershipSuccess } from './MembershipAlerts';
import { msInput, msPrimaryBtn, msSecondaryBtn } from './membershipUi';

const MembershipToolsPanel: React.FC = () => {
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [activateUserId, setActivateUserId] = useState('');
  const [activatePlanId, setActivatePlanId] = useState('');
  const [gstPercent, setGstPercent] = useState(0);
  const [gstMode, setGstMode] = useState<'inclusive' | 'exclusive'>('exclusive');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadPlans = useCallback(async () => {
    setPlans(await fetchMembershipPlans());
  }, []);

  const loadGst = useCallback(async () => {
    const settings = await fetchMembershipGstSettings();
    setGstPercent(Number(settings.gstPercent || 0));
    setGstMode(settings.gstMode === 'inclusive' ? 'inclusive' : 'exclusive');
  }, []);

  useEffect(() => {
    void Promise.all([loadPlans(), loadGst()]).catch((e: any) => {
      setError(e?.response?.data?.message || 'Failed to load tools');
    });
  }, [loadPlans, loadGst]);

  const gstAmountFor = (price: number) => {
    const rate = Math.max(0, Number(gstPercent) || 0);
    if (rate <= 0) return 0;
    if (gstMode === 'inclusive') {
      return Math.round((price * rate) / (100 + rate) * 100) / 100;
    }
    return Math.round((price * rate) / 100 * 100) / 100;
  };

  const payableFor = (price: number) => {
    const gst = gstAmountFor(price);
    if (gstPercent <= 0) return price;
    return gstMode === 'inclusive' ? price : Math.round((price + gst) * 100) / 100;
  };

  return (
    <MembershipPanelShell title="Tools">
      <MembershipError message={error} />
      <MembershipSuccess message={success} />

      <div className="space-y-4">
        <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">GST settings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Keep GST at 0 to hide it on app and web. When you set 1% or more, GST is shown and the payable amount is calculated as exclusive or inclusive.
          </p>
          <div className="mt-4 grid max-w-xl gap-3">
            <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-200">
              GST %
              <input
                type="number"
                min={0}
                step="0.01"
                value={gstPercent}
                onChange={(e) => setGstPercent(Number(e.target.value))}
                className={msInput}
              />
            </label>
            <div className="text-sm text-gray-700 dark:text-gray-200">
              GST type
              <div className="mt-2 flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="tools-gst-mode"
                    checked={gstMode === 'exclusive'}
                    onChange={() => setGstMode('exclusive')}
                  />
                  Exclusive (add GST on top of plan price)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="tools-gst-mode"
                    checked={gstMode === 'inclusive'}
                    onChange={() => setGstMode('inclusive')}
                  />
                  Inclusive (GST already in plan price)
                </label>
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-900 dark:text-gray-300">
              {gstPercent > 0
                ? `Example on ₹1,000: GST ₹${gstAmountFor(1000).toLocaleString()} · customer pays ₹${payableFor(1000).toLocaleString()}`
                : 'GST is 0, so customers will not see a GST line.'}
            </div>
            <button
              type="button"
              disabled={busy}
              className={`${msPrimaryBtn} w-fit`}
              onClick={async () => {
                try {
                  setBusy(true);
                  setError(null);
                  const result = await updateMembershipGstSettings({
                    gstPercent: Math.max(0, Number(gstPercent) || 0),
                    gstMode,
                  });
                  setSuccess(result?.message || 'GST settings saved on all plans.');
                  await loadPlans();
                } catch (e: any) {
                  setError(e?.response?.data?.message || 'Failed to save GST settings');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save GST
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Manual activate</h3>
          <p className="mt-1 text-sm text-gray-500">Grant a plan to a user without a payment.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <input
              value={activateUserId}
              onChange={(e) => setActivateUserId(e.target.value)}
              placeholder="User ID"
              className={`${msInput} min-w-[220px]`}
            />
            <select
              value={activatePlanId}
              onChange={(e) => setActivatePlanId(e.target.value)}
              className={msInput}
            >
              <option value="">Select plan</option>
              {plans.map((p) => (
                <option key={p.membershipPlanId} value={p.membershipPlanId}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy}
              className={msPrimaryBtn}
              onClick={async () => {
                try {
                  setBusy(true);
                  setError(null);
                  if (!plans.length) await loadPlans();
                  await manualActivateMembership({
                    userId: activateUserId.trim(),
                    membershipPlanId: activatePlanId,
                    reason: 'Admin manual activation',
                  });
                  setSuccess('Membership activated.');
                  setActivateUserId('');
                } catch (e: any) {
                  setError(e?.response?.data?.message || 'Activation failed');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Activate
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Catalog & jobs</h3>
          <p className="mt-1 text-sm text-gray-500">Seed the default catalog or run expiry processing.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              className={msPrimaryBtn}
              onClick={async () => {
                try {
                  setBusy(true);
                  await seedMembershipPlans();
                  setSuccess('Default Silver, Gold, and Platinum plans seeded.');
                  await loadPlans();
                } catch (e: any) {
                  setError(e?.response?.data?.message || 'Failed to seed plans');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Seed default plans
            </button>
            <button
              type="button"
              disabled={busy}
              className={msSecondaryBtn}
              onClick={async () => {
                try {
                  setBusy(true);
                  const result = await runMembershipExpiryJob();
                  setSuccess(`Expiry job completed. ${JSON.stringify(result || {})}`);
                } catch (e: any) {
                  setError(e?.response?.data?.message || 'Expiry job failed');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Run expiry job
            </button>
          </div>
        </section>
      </div>
    </MembershipPanelShell>
  );
};

export default MembershipToolsPanel;
